// Free shipping top-up link.
//
// UpCart shows "You're €X away from free shipping" in its cart drawer. This script
// works out that same X (using UpCart's own reward tier settings and the same maths
// UpCart's rewards bar uses), rounds it UP to the nearest 10 and builds a link to
// /collections/all with the price filter's max value prefilled, e.g.
//
//   €37.50 away  ->  /collections/all?filter.v.price.lte=40
//
// The link is:
//   1. injected into the UpCart drawer directly under the rewards message
//   2. written to any theme element carrying [data-topup-link] (href + text, and
//      `hidden` is toggled so it only shows while there is an amount to top up)
//   3. exposed on window.topUp.state and broadcast via a `topup:updated` event
//
// UpCart hooks used (all public):
//   window.upcartSettings.upcartEditorSettings.rewardsTiers  - tier config
//   window.upcartSubscribeCartLoaded / CartUpdated / CartOpened - cart events
//   window.upcartDocumentOrShadowRoot                       - drawer root (shadow DOM)
//   window.upcartMoneyFormat                                - e.g. "€{{amount_with_comma_separator}}"

(function () {
  const ROUND_TO = 10;
  const COLLECTION_URL = '/collections/all';
  const LINK_CLASS = 'topup-link';
  const LINK_TEXT = 'Shop items under {{amount}}';
  const REWARDS_MESSAGE_SELECTOR = '.upcart-rewards-message';

  let state = null;
  let latestCart = null;

  // ---------------------------------------------------------------------------
  // UpCart settings
  // ---------------------------------------------------------------------------

  function getShippingTiers() {
    const editor = window.upcartSettings && window.upcartSettings.upcartEditorSettings;
    if (!editor || !editor.rewardsModule) return { tiers: [], usePreDiscountedTotal: false };

    const config = editor.rewardsTiers || {};
    let tiers = Array.isArray(config.tiers) ? config.tiers.slice() : [];

    // UpCart falls back to a single legacy "Free Shipping" tier when none are configured
    if (tiers.length === 0 && editor.rewardsMinAmount) {
      tiers = [{ targetType: 'shipping', minAmount: editor.rewardsMinAmount }];
    }

    tiers = tiers
      .filter((tier) => tier.targetType === 'shipping')
      .map((tier) => ({ ...tier, minAmountCents: Math.round(Number(tier.minAmount) * 100) }))
      .filter((tier) => tier.minAmountCents > 0)
      .sort((a, b) => a.minAmountCents - b.minAmountCents);

    return { tiers, usePreDiscountedTotal: Boolean(config.usePreDiscountedTotal) };
  }

  // Mirrors UpCart's rewards basis: discounted (or pre-discount) total, minus gift cards
  function getCartValueCents(cart, usePreDiscountedTotal) {
    const giftCards = (cart.items || []).reduce(
      (sum, item) => (item.gift_card ? sum + item.final_line_price : sum),
      0
    );
    const base = usePreDiscountedTotal ? cart.original_total_price : cart.total_price;
    return base - giftCards;
  }

  // ---------------------------------------------------------------------------
  // Money formatting - reuses UpCart's money format so the symbol/position matches
  // ---------------------------------------------------------------------------

  function formatWholeAmount(amount) {
    const format = window.upcartMoneyFormat || (window.theme && window.theme.moneyFormat) || '€{{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, String(amount)).replace(/<[^>]*>/g, '').trim();
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  function computeState(cart) {
    if (!cart) return null;
    const editor = window.upcartSettings?.upcartEditorSettings || {};
    if (editor.rewardsBasis === 'itemCount') return null;
    if (!cart.item_count && !editor.rewardsShowOnEmptyCart) return null;

    const { tiers, usePreDiscountedTotal } = getShippingTiers();
    if (tiers.length === 0) return null;

    const cartValueCents = getCartValueCents(cart, usePreDiscountedTotal);
    const nextTier = tiers.find((tier) => cartValueCents < tier.minAmountCents);
    if (!nextTier) return null; // free shipping already unlocked

    const remainingCents = nextTier.minAmountCents - cartValueCents;
    const maxPrice = Math.ceil(remainingCents / (ROUND_TO * 100)) * ROUND_TO;

    return {
      remainingCents,
      remaining: remainingCents / 100,
      maxPrice,
      maxPriceFormatted: formatWholeAmount(maxPrice),
      thresholdCents: nextTier.minAmountCents,
      url: `${COLLECTION_URL}?filter.v.price.lte=${maxPrice}`,
    };
  }

  function update(cart) {
    if (cart) latestCart = cart;
    state = computeState(latestCart);
    window.topUp.state = state;
    renderInDrawer();
    renderThemeLinks();
    document.dispatchEvent(new CustomEvent('topup:updated', { detail: state }));
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  function linkText() {
    return LINK_TEXT.replace('{{amount}}', state.maxPriceFormatted);
  }

  function getDrawerRoot() {
    return window.upcartDocumentOrShadowRoot || document;
  }

  function ensureDrawerStyles(root) {
    if (root.querySelector(`style[data-${LINK_CLASS}]`)) return;
    const style = document.createElement('style');
    style.setAttribute(`data-${LINK_CLASS}`, '');
    style.textContent = `
      .${LINK_CLASS} {
        display: inline-block;
        margin-top: 6px;
        font-size: 13px;
        color: inherit;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    `;
    root.appendChild(style);
  }

  function renderInDrawer() {
    const root = getDrawerRoot();
    const message = root.querySelector(REWARDS_MESSAGE_SELECTOR);
    let link = root.querySelector(`.${LINK_CLASS}`);

    if (!state || !message) {
      if (link) link.remove();
      return;
    }

    ensureDrawerStyles(root);

    if (!link) {
      link = document.createElement('a');
      link.className = LINK_CLASS;
    }
    // Only write on change - the MutationObserver below would otherwise re-fire
    const text = linkText();
    if (link.getAttribute('href') !== state.url) link.setAttribute('href', state.url);
    if (link.textContent !== text) link.textContent = text;

    // UpCart re-renders the message via React, so re-attach if we've been dropped
    if (link.previousElementSibling !== message) {
      message.insertAdjacentElement('afterend', link);
    }
  }

  function renderThemeLinks() {
    document.querySelectorAll('[data-topup-link]').forEach((el) => {
      if (!state) {
        el.hidden = true;
        return;
      }
      const template = el.getAttribute('data-topup-text') || LINK_TEXT;
      el.setAttribute('href', state.url);
      el.textContent = template.replace('{{amount}}', state.maxPriceFormatted);
      el.hidden = false;
    });
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  function watchDrawer() {
    const root = getDrawerRoot();
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled || !state) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        renderInDrawer();
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function subscribe() {
    window.upcartSubscribeCartLoaded((detail) => update(detail && detail.cart));
    window.upcartSubscribeCartUpdated((detail) => update(detail && detail.cart));
    window.upcartSubscribeCartOpened(() => update());
    watchDrawer();

    // Cover the case where UpCart loaded the cart before we subscribed
    if (window.upcartPreloadedCart) update(window.upcartPreloadedCart);
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => update(cart))
      .catch(() => {});
  }

  function waitForUpcart(attempts = 300) {
    if (typeof window.upcartSubscribeCartLoaded === 'function' && window.upcartDocumentOrShadowRoot) {
      subscribe();
    } else if (attempts > 0) {
      setTimeout(() => waitForUpcart(attempts - 1), 100);
    }
  }

  window.topUp = { state: null, refresh: () => update() };
  waitForUpcart();
})();
